"use client";

import { useState } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatchKickoffCountdown } from "@/hooks/useMatchKickoffCountdown";
import { LIVE_STATUSES } from "@/contants";
import { formatUnixToVNDate, formatHandicapLabel } from "@/utils/format";
import {
    formatCorrectScoresForGrid,
    splitCorrectScoresListToSections,
    getMatchDisplayTime,
} from "@/utils/matchInfo";

import ViuGoOddsMatchRowMainContent from "./ViuGoOddsMatchRowMainContent";
import ViuGoOddsMatchRowFooter from "./ViuGoOddsMatchRowFooter";
import ViuGoOtherBetsDropdown from "../ViuGoOtherBetsDropdown";
import ViuGoTipsAiModal from "../ViuGoTipsAiModal";

function formatValue(value) {
    if (value === null || value === undefined || value === "") return "-";
    return value;
}

export default function ViuGoOddsMatchRow({
    match,
    isFirstInLeague = false,
    isLastInLeague = false,
}) {
    const { getMatchOdds, getMatchInsight } = useMatches();
    const { getScore, getOdds } = useLiveDataForMatch(match.id);
    const [openOtherOdds, setOpenOtherOdds] = useState(false);
    const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
    const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
    const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
    const [otherOddsData, setOtherOddsData] = useState(null);
    const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
    const [mobileOddsSlide, setMobileOddsSlide] = useState(0);
    const companyIdOdds = useSelector((state) => state.bookmakerReducer);

    const liveScore = getScore();
    const liveOdds = getOdds();
    const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);
    const {
        currentHomeScore,
        currentAwayScore,
        currentHomeCorner,
        currentAwayCorner,
        homeYellow,
        awayYellow,
        homeRed,
        awayRed,
    } = useMatchData(liveScore, match);
    const odds = useMatchOdds(match, filterOdds, companyIdOdds);

    const statusNum =
        liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
    const isLive = LIVE_STATUSES.includes(statusNum);
    const { countdownDisplay: betPromoCountdown } = useMatchKickoffCountdown(match?.matchTime, {
        isLive,
    });
    const betPromoSvgText = isLive ? "" : betPromoCountdown || "2444";
    const { date: formattedDate, gio: formattedGio } = formatUnixToVNDate(match?.matchTime);

    const handleOtherOddsClick = () => {
        setOpenOtherOdds((prev) => !prev);
        if (!openOtherOdds && !otherOddsData) {
            setLoadingOtherOdds(true);
            getMatchOdds(match.id, { include: "otherOdd" })
                .then((data) => {
                    if (data?.success) setOtherOddsData(data.data);
                })
                .finally(() => setLoadingOtherOdds(false));
        }
    };

    const handleOpenTipsAiModal = async () => {
        setOpenTipsAiModal(true);
        if (tipsAiInsightData || loadingTipsAiInsight) return;

        setLoadingTipsAiInsight(true);
        try {
            const insightResponse = await getMatchInsight(match?.id);
            if (insightResponse?.success && insightResponse?.data) {
                setTipsAiInsightData(insightResponse.data);
            }
        } finally {
            setLoadingTipsAiInsight(false);
        }
    };

    const goalOddEvenOdds = otherOddsData?.goalOddEvenOdds ?? [];
    const correctScoresListFull = otherOddsData?.correctScoresOdds
        ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
        : [];
    const correctScoresHalf = splitCorrectScoresListToSections(
        otherOddsData?.correctScoresHalfOdds
            ? formatCorrectScoresForGrid(otherOddsData.correctScoresHalfOdds)
            : [],
    );
    const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
    const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds)
        ? otherOddsData.totalCornersOdds
        : [];
    const totalCornersHalfOdds = Array.isArray(otherOddsData?.totalCornersHalfOdds)
        ? otherOddsData.totalCornersHalfOdds
        : [];
    const goalOddEvenFirst = goalOddEvenOdds?.[0] ?? {};
    const hasOtherData =
        goalOddEvenOdds?.length > 0 ||
        correctScoresListFull.length > 0 ||
        totalCornersOdds.length > 0 ||
        totalCornersHalfOdds.length > 0;

    const otherCount = Number(match.countOtherOdds ?? 0);
    const svgIdSuffix = String(match?.id ?? "row").replace(/[^a-zA-Z0-9_-]/g, "_");
    const betFilterId = `viugo_bet_filter_${svgIdSuffix}`;
    const betGradientPrimaryId = `viugo_bet_gradient_primary_${svgIdSuffix}`;
    const betGradientTextId = `viugo_bet_gradient_text_${svgIdSuffix}`;
    const betGradientBodyId = `viugo_bet_gradient_body_${svgIdSuffix}`;
    const betGradientCircleId = `viugo_bet_gradient_circle_${svgIdSuffix}`;
    const leagueName =
        match?.league?.name || match?.leagueName || "Copa Libertadores";
    const leagueLogo =
        match?.league?.logo ||
        match?.leagueLogo ||
        "";
        
    const homeLogo =
        match?.homeTeam?.logo ||
        match?.homeLogo ||
        "";
    const awayLogo =
        match?.awayTeam?.logo ||
        match?.awayLogo ||
        "";
    const displayTimeMobile = getMatchDisplayTime(
        liveScore,
        statusNum,
        match?.matchTime,
        match?.halfStartTime,
    );
    const modalStatusLabel = !isLive
        ? "Sắp diễn ra"
        : displayTimeMobile?.type === "minute"
          ? displayTimeMobile.halfLabel && displayTimeMobile.halfLabel !== "--"
              ? displayTimeMobile.halfLabel
              : "Trực tiếp"
          : displayTimeMobile?.type === "var"
            ? "VAR"
            : (displayTimeMobile?.content ?? "Trực tiếp");
    const scoreText = `${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`;

    const ftOddsColumnProps = {
        handicapLabel: formatHandicapLabel(odds.handicapValue, true),
        homeHandicap: formatValue(odds.homeHandicapOdds),
        homeHandicapTrend: odds.homeHandicapTrend,
        awayHandicap: formatValue(odds.awayHandicapOdds),
        awayHandicapTrend: odds.awayHandicapTrend,
        overUnderLine: odds.overUnderValue,
        overOdds: formatValue(odds.overOdds),
        overTrend: odds.overTrend,
        underOdds: formatValue(odds.underOdds),
        underTrend: odds.underTrend,
        oneX2Home: formatValue(odds.homeEuropeOdds),
        oneX2HomeTrend: odds.homeEuropeTrend,
        oneX2Draw: formatValue(odds.instantDrawOdds),
        oneX2DrawTrend: odds.drawTrend,
        oneX2Away: formatValue(odds.awayEuropeOdds),
        oneX2AwayTrend: odds.awayEuropeTrend,
    };
    const htOddsColumnProps = {
        handicapLabel: formatHandicapLabel(odds.handicapHalfValue, true),
        homeHandicap: formatValue(odds.homeHandicapHalfOdds),
        homeHandicapTrend: odds.homeHandicapHalfTrend,
        awayHandicap: formatValue(odds.awayHandicapHalfOdds),
        awayHandicapTrend: odds.awayHandicapHalfTrend,
        overUnderLine: odds.overUnderHalfValue,
        overOdds: formatValue(odds.overHalfOdds),
        overTrend: odds.overHalfTrend,
        underOdds: formatValue(odds.underHalfOdds),
        underTrend: odds.underHalfTrend,
        oneX2Home: formatValue(odds.homeEuropeHalfOdds),
        oneX2HomeTrend: odds.homeEuropeHalfTrend,
        oneX2Draw: formatValue(odds.drawHalfOdds),
        oneX2DrawTrend: odds.drawHalfTrend,
        oneX2Away: formatValue(odds.awayEuropeHalfOdds),
        oneX2AwayTrend: odds.awayEuropeHalfTrend,
    };

    const rowWrapClass = [
        "group/item bg-white",
        isFirstInLeague &&
            "border-t border-solid border-[var(--theme-border)]",
        isLastInLeague
            ? "kbv-shadow-soft-indigo-slate mb-[5px] rounded-[8px]"
            : "border-b border-solid border-[var(--theme-border)]",
    ]
        .filter(Boolean)
        .join(" ");

    const footerBottomWhenLastClass = isLastInLeague
        ? "[border-bottom:1px_solid_var(--theme-border)]"
        : "";

    return (
        <div className={rowWrapClass}>
            <div className="flex flex-col items-stretch overflow-visible">
                <ViuGoOddsMatchRowMainContent
                    match={match}
                    isLive={isLive}
                    formattedDate={formattedDate}
                    formattedGio={formattedGio}
                    displayTimeMobile={displayTimeMobile}
                    currentHomeScore={currentHomeScore}
                    currentAwayScore={currentAwayScore}
                    currentHomeCorner={currentHomeCorner}
                    currentAwayCorner={currentAwayCorner}
                    homeYellow={homeYellow}
                    awayYellow={awayYellow}
                    homeRed={homeRed}
                    awayRed={awayRed}
                    mobileOddsSlide={mobileOddsSlide}
                    setMobileOddsSlide={setMobileOddsSlide}
                    ftOddsColumnProps={ftOddsColumnProps}
                    htOddsColumnProps={htOddsColumnProps}
                    liveScore={liveScore}
                    statusNum={statusNum}
                />
                <ViuGoOddsMatchRowFooter
                    footerBottomWhenLastClass={footerBottomWhenLastClass}
                    onOpenTipsAiModal={handleOpenTipsAiModal}
                    onOtherOddsClick={handleOtherOddsClick}
                    otherCount={otherCount}
                    isLive={isLive}
                    betFilterId={betFilterId}
                    betGradientPrimaryId={betGradientPrimaryId}
                    betGradientTextId={betGradientTextId}
                    betGradientBodyId={betGradientBodyId}
                    betGradientCircleId={betGradientCircleId}
                    betPromoSvgText={betPromoSvgText}
                />
            </div>

            {openOtherOdds && (
                <ViuGoOtherBetsDropdown
                    loadingOtherOdds={loadingOtherOdds}
                    otherOddsData={otherOddsData}
                    hasOtherData={hasOtherData}
                    isLive={isLive}
                    goalOddEvenOdds={goalOddEvenOdds}
                    goalOddEvenFirst={goalOddEvenFirst}
                    correctScoresListFull={correctScoresListFull}
                    correctScoresFull={correctScoresFull}
                    correctScoresHalf={correctScoresHalf}
                    totalCornersOdds={totalCornersOdds}
                    totalCornersHalfOdds={totalCornersHalfOdds}
                    odds={odds}
                    fontBody="text-[13px]"
                />
            )}

            <ViuGoTipsAiModal
                open={openTipsAiModal}
                onClose={() => setOpenTipsAiModal(false)}
                match={match}
                matchInfo={{
                    formattedDate,
                    formattedGio,
                    isLive,
                }}
                statsInfo={{
                    currentHomeScore,
                    currentAwayScore,
                    homeYellow,
                    homeRed,
                    awayYellow,
                    awayRed,
                    currentHomeCorner,
                    currentAwayCorner,
                }}
                oddsInfo={{
                    handicapValue: odds.handicapValue,
                    homeHandicapOdds: odds.homeHandicapOdds,
                    awayHandicapOdds: odds.awayHandicapOdds,
                    overUnderValue: odds.overUnderValue,
                    overOdds: odds.overOdds,
                    underOdds: odds.underOdds,
                    homeEuropeOdds: odds.homeEuropeOdds,
                    awayEuropeOdds: odds.awayEuropeOdds,
                    instantDrawOdds: odds.instantDrawOdds,
                    handicapHalfValue: odds.handicapHalfValue,
                    homeHandicapHalfOdds: odds.homeHandicapHalfOdds,
                    awayHandicapHalfOdds: odds.awayHandicapHalfOdds,
                }}
                leagueName={leagueName}
                leagueLogo={leagueLogo}
                homeName={match.homeName}
                homeLogo={homeLogo}
                awayName={match.awayName}
                awayLogo={awayLogo}
                matchDate={formattedDate}
                matchTime={formattedGio}
                matchTimestamp={match?.matchTime}
                headerScore={scoreText}
                liveScoreToday={liveScore}
                matchStatusNum={statusNum}
                matchTimeUnix={match?.matchTime}
                halfStartTime={match?.halfStartTime}
                isMatchLive={isLive}
                statusLabel={modalStatusLabel}
                insightData={tipsAiInsightData}
                insightLoading={loadingTipsAiInsight}
            />
        </div>
    );
}

